import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { getLogStreamMongoData } from "../../../services/mongo/taskLogs";
import { getLogEvents } from "../../../services/aws/cloudwatch";
import { LOG_COLORS, LOG_LEVELS } from "../constants";

// Helper to determine log severity level
const determineLogLevel = (message) => {
  const logInfo = message.match(/\[ [0-9].* \]/gm);
  if (!logInfo) return LOG_LEVELS.NONE;

  const logMessage = logInfo[0];
  if (logMessage.includes(LOG_LEVELS.ERROR) || logMessage.includes("CRITICAL")) {
    return LOG_LEVELS.ERROR;
  } else if (logMessage.includes(LOG_LEVELS.WARNING)) {
    return LOG_LEVELS.WARNING;
  } else if (logMessage.includes(LOG_LEVELS.EXTRA)) {
    return LOG_LEVELS.EXTRA;
  } else if (logMessage.includes(LOG_LEVELS.DEBUG)) {
    return LOG_LEVELS.DEBUG;
  } else if (logMessage.includes(LOG_LEVELS.INFO)) {
    return LOG_LEVELS.INFO;
  }
  return LOG_LEVELS.NONE;
};

// Helper to format log message into JSX
const formatLogMessage = (message, logLevel, index) => {
  const textWithBreaks = message.split(/\r?\n|\r|\n/g).map((line, idx) => (
    <div key={`line-${idx}`}>
      {line.replaceAll(" ", "\xa0")}
      <br key={`break1-${idx}`} />
    </div>
  ));

  return {
    element: (
      <Box
        key={index}
        sx={{
          color: LOG_COLORS[logLevel],
          opacity: "97%",
          display: "inline-block",
        }}
        loglevel={logLevel}
      >
        {textWithBreaks}
      </Box>
    ),
    lineBreak: <br key={`break2-${index}`} loglevel={logLevel} />,
  };
};

/**
 * Custom hook to manage fetching and processing log stream data
 *
 * @returns {Object} Log stream data and related functions
 */
export default function useLogStreamData() {
  const [logStreamName, setLogStreamName] = useState("");
  const [errorCount, setErrorCount] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [logStreamElements, setLogStreamElements] = useState([]);
  const [logStreamMongoData, setLogStreamMongoData] = useState([]);
  const [taskFinished, setTaskFinished] = useState(false);
  const refreshIntervalRef = useRef(null);

  const createLogElements = (logStream) => {
    const logCounts = { error: 0, warning: 0 };
    const elements = [];

    // Process each log entry
    logStream.forEach((eventLog, index) => {
      // Classify log by severity level
      const logLevel = determineLogLevel(eventLog.message);

      // Update counters
      if (logLevel === LOG_LEVELS.ERROR) logCounts.error++;
      if (logLevel === LOG_LEVELS.WARNING) logCounts.warning++;

      // Format and add log elements
      const formattedLog = formatLogMessage(eventLog.message, logLevel, index);
      elements.push(formattedLog.element);
      elements.push(formattedLog.lineBreak);
    });

    // Update state
    setErrorCount(logCounts.error);
    setWarningCount(logCounts.warning);

    return elements;
  };

  /**
   * Synchronizes log data from both CloudWatch and MongoDB
   * Fetches current log events and updates UI elements
   * Checks task status and stops polling if task has completed
   * @returns {Promise<void>}
   */
  const syncLogs = async () => {
    // Extract log stream name from URL path
    const name = decodeURIComponent(window.location.pathname.replace("/logs/", ""));
    setLogStreamName(name);

    // Fetch log stream events from CloudWatch
    const logStreamRes = await getLogEvents(name);
    const logStreamJson = await logStreamRes.json();
    if (logStreamJson) {
      // Convert raw log data into styled React elements
      setLogStreamElements(createLogElements(logStreamJson.events));
    }

    // Fetch additional task metadata from MongoDB
    const taskLogDataRes = await getLogStreamMongoData(name);
    const taskLogDataJson = await taskLogDataRes.json();
    if (taskLogDataJson.log_stream_data !== null) {
      // Update MongoDB-sourced task data state
      setLogStreamMongoData(taskLogDataJson.log_stream_data);

      // If task has finished, update state and stop polling
      if (taskLogDataJson.log_stream_data["Task State"] === "Stopped") {
        setTaskFinished(true);
        clearInterval(refreshIntervalRef.current);
      }
    }
  };

  const initRefreshInterval = () => {
    // Refresh log stream every 60 seconds
    refreshIntervalRef.current = setInterval(() => {
      syncLogs();
    }, 60000);
  };

  useEffect(() => {
    syncLogs();
    initRefreshInterval();
    // Clear on component unmount
    return () => clearInterval(refreshIntervalRef.current);
  }, []);

  return {
    logStreamName,
    errorCount,
    warningCount,
    logStreamElements,
    logStreamMongoData,
    taskFinished,
    syncLogs,
  };
}
